import { useEffect } from "react";
import { X as XIcon } from "lucide-react";

// =====================================================================
// SidePanel — slide-in from the right (desktop) / bottom-sheet (mobile).
// Replaces the centered-modal pattern for the calendar's
// new-appointment + appointment-detail flows.
//
// Behavior:
//   * Closed by default; opens when `open` is true
//   * Click outside (overlay) → onClose
//   * Esc key → onClose
//   * Stays mounted while open so transitions stay smooth
// =====================================================================

export default function SidePanel({ open, onClose, title, children }) {
  useEffect(() => {
    if (!open) return;
    function onEsc(e) { if (e.key === "Escape") onClose?.(); }
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 pointer-events-none"
      data-testid="calendar-side-panel"
    >
      {/* Click-outside overlay (desktop only — on mobile, full-width sheet has its own backdrop) */}
      <div
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm pointer-events-auto"
        onClick={onClose}
        aria-hidden
      />

      {/* Panel */}
      <div
        className="absolute right-0 top-0 h-full w-full sm:max-w-md bg-white shadow-2xl pointer-events-auto flex flex-col"
        role="dialog" aria-modal="true"
      >
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <h2 className="font-semibold text-slate-900 text-sm">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="side-panel-close"
            className="p-1 rounded hover:bg-slate-200"
          >
            <XIcon className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}
